import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { 
  LayoutDashboard, 
  ChefHat, 
  Package, 
  Bell, 
  User,
  LogOut,
  Store
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';

export default function SellerLayout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();

  const isDealer = user?.role === 'dealer';
  const basePath = isDealer ? '/dealer' : '/seller';
  const title = isDealer ? 'RuchiBox Vendor' : 'RuchiBox Seller';

  const navItems = [
    { path: basePath, icon: LayoutDashboard, label: 'Dashboard' },
    { path: `${basePath}/items`, icon: ChefHat, label: 'My Items' },
    { path: `${basePath}/orders`, icon: Package, label: 'Orders' },
    { path: `${basePath}/notifications`, icon: Bell, label: 'Alerts' },
  ];

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const profilePhotoUrl = user?.profile?.photo_url;

  return (
    <div className="min-h-screen bg-background">
      {/* Desktop Sidebar + Header */}
      <div className="hidden md:flex">
        {/* Sidebar */}
        <aside className="w-64 h-screen fixed left-0 top-0 bg-card border-r border-border p-4 flex flex-col">
          <div className="flex items-center gap-2 mb-8">
            {isDealer ? (
              <Store className="w-8 h-8 text-primary" />
            ) : (
              <ChefHat className="w-8 h-8 text-primary" />
            )}
            <span className="font-heading text-xl font-bold">{title}</span>
          </div>
          
          <nav className="flex-1 space-y-1">
            {navItems.map((item) => (
              <Button
                key={item.path}
                variant={location.pathname === item.path ? 'secondary' : 'ghost'}
                onClick={() => navigate(item.path)}
                className="w-full justify-start"
              >
                <item.icon className="w-5 h-5 mr-3" />
                {item.label}
              </Button>
            ))}
          </nav>

          <div className="border-t border-border pt-4">
            <div className="flex items-center gap-3 mb-3">
              <Avatar>
                {profilePhotoUrl && <AvatarImage src={profilePhotoUrl} alt={user?.name} />}
                <AvatarFallback className="bg-primary text-primary-foreground">
                  {user?.name?.charAt(0) || 'S'}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="font-semibold truncate">{user?.name}</p>
                <p className="text-xs text-muted-foreground">{user?.mobile}</p>
              </div>
            </div>
            <Button 
              variant="ghost" 
              onClick={() => navigate(`${basePath}/profile`)} 
              className="w-full justify-start mb-2"
            >
              <User className="w-4 h-4 mr-2" />
              My Profile
            </Button>
            <Button variant="ghost" onClick={handleLogout} className="w-full justify-start text-destructive">
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </aside>

        {/* Main Content */}
        <main className="ml-64 flex-1 p-8">
          {children}
        </main>
      </div>

      {/* Mobile Layout */}
      <div className="md:hidden">
        {/* Mobile Header */}
        <header className="glass-header sticky top-0 z-50">
          <div className="px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              {isDealer ? (
                <Store className="w-6 h-6 text-primary" />
              ) : (
                <ChefHat className="w-6 h-6 text-primary" />
              )}
              <span className="font-heading text-lg font-bold">{title}</span>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="rounded-full">
                  <Avatar className="h-8 w-8">
                    {profilePhotoUrl && <AvatarImage src={profilePhotoUrl} alt={user?.name} />}
                    <AvatarFallback className="bg-primary text-primary-foreground text-sm">
                      {user?.name?.charAt(0) || 'S'}
                    </AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <div className="px-2 py-1.5">
                  <p className="font-semibold text-sm">{user?.name}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate(`${basePath}/profile`)}>
                  <User className="w-4 h-4 mr-2" />
                  My Profile
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleLogout} className="text-destructive">
                  <LogOut className="w-4 h-4 mr-2" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Mobile Content */}
        <main className="p-4 pb-20">
          {children}
        </main>

        {/* Mobile Bottom Navigation */}
        <nav className="mobile-nav">
          {navItems.map((item) => (
            <button
              key={item.path}
              onClick={() => navigate(item.path)}
              className={`mobile-nav-item ${location.pathname === item.path ? 'active' : ''}`}
            >
              <item.icon className="w-5 h-5" />
              <span className="text-xs">{item.label}</span>
            </button>
          ))}
        </nav>
      </div>
    </div>
  );
}
